import type { EventState } from '../api/public'

/**
 * What an event's state says, and how long until the next read.
 *
 * # One phrasing, shared
 *
 * `STATE_LABEL` was private to `Schedule.tsx`. It holds the argued-over
 * `provisional` wording below, so a second page writing its own words is how
 * two views of one week come to describe it differently -- which is the class
 * of problem the consolidated event page exists to end.
 *
 * # Polling is a property of the STATE, not of the page
 *
 * A round nobody is playing has nothing to report, and a view that keeps asking
 * is a tab refreshing forever over a record that cannot change -- which is what
 * the round tracker does today. `in_progress` is the only state where holes are
 * arriving. `paused` still polls, slowly: nothing is completed during a pause
 * (FR-054), so there is nothing to collect, but the transition OUT of one is
 * exactly what a reader at the simulator is waiting for, and a page that has
 * stopped asking never learns of it. A provisional week's make-up may be weeks
 * away; it is read, not watched.
 */

/** How each state reads to a member. FR-059 makes it visible everywhere. */
export const STATE_LABEL: Record<EventState, string> = {
  scheduled: 'Scheduled',
  in_progress: 'Playing now',
  paused: 'Paused',
  // **Not "Final", and not "awaiting a round" either** (FR-071a). A provisional
  // week has been scored and its points are in the standings; what it is NOT is
  // settled. It said "awaiting a round", which was true while only a missing
  // card kept a week open — and became false under FR-066a, where a week whose
  // field is complete still sits provisional until the commissioner closes it.
  // **A label that is sometimes false is worse than a vaguer one that is always
  // true.** Who is awaited is the commissioner's business and is in the console.
  provisional: 'Scored — not final',
  complete: 'Final',
}

/** Milliseconds until the next read, or null when there is nothing more to ask for. */
export function nextPoll(state: EventState): number | null {
  switch (state) {
    case 'in_progress':
      return 10_000
    case 'paused':
      return 60_000
    default:
      return null
  }
}
